export type WorkspaceView = "overview" | "networth" | "positions" | "cashflow" | "fx" | "planning" | "research" | "data";

export type WorkspaceRouteKey =
  | "overview"
  | "networth"
  | "positions-overview"
  | "positions-holdings"
  | "positions-allocation"
  | "positions-maturities"
  | "cashflow"
  | "fx"
  | "research-funds"
  | "research-wealth"
  | "research-macro"
  | "research-macro-cn"
  | "data-connections"
  | "data-import"
  | "data-records"
  | "data-backup"
  | "data-security"
  | "data-settings";

export const WORKSPACE_NAVIGATION_EVENT = "qy:workspace-navigation";

export type WorkspaceIcon =
  | "overview"
  | "assets"
  | "networth"
  | "positions"
  | "groups"
  | "maturity"
  | "cashflow"
  | "fx"
  | "planning"
  | "research"
  | "funds"
  | "wealth"
  | "macro"
  | "data"
  | "connections"
  | "import"
  | "records"
  | "backup"
  | "security"
  | "settings";

export type WorkspaceTask = {
  id: WorkspaceRouteKey;
  view: WorkspaceView;
  section?: string;
  label: string;
  caption: string;
  icon: WorkspaceIcon;
};

export type WorkspaceNavigationGroup = {
  id: "overview" | "assets" | "planning" | "research" | "data";
  label: string;
  caption: string;
  icon: WorkspaceIcon;
  task?: WorkspaceTask;
  children?: readonly WorkspaceTask[];
};

export const workspaceNavigation: readonly WorkspaceNavigationGroup[] = [
  {
    id: "overview",
    label: "总览",
    caption: "家庭资产、投资表现与近期行动",
    icon: "overview",
    task: {
      id: "overview",
      view: "overview",
      label: "家庭总览",
      caption: "家庭资产、投资表现与近期行动",
      icon: "overview",
    },
  },
  {
    id: "assets",
    label: "资产与账本",
    caption: "净值、持仓与资金记录",
    icon: "assets",
    children: [
      { id: "networth", view: "networth", label: "月度净值", caption: "维护每月权威家庭净值归档", icon: "networth" },
      { id: "positions-overview", view: "positions", section: "overview", label: "投资概览", caption: "当前资产与含清仓历史收益", icon: "overview" },
      { id: "positions-holdings", view: "positions", section: "holdings", label: "持仓明细", caption: "搜索、筛选并核验每项资产", icon: "positions" },
      { id: "positions-allocation", view: "positions", section: "allocation", label: "资产配置", caption: "资产组、组合归属与全周期收益", icon: "groups" },
      { id: "positions-maturities", view: "positions", section: "maturities", label: "到期日程", caption: "到期、开放与费用节点", icon: "maturity" },
      { id: "cashflow", view: "cashflow", label: "收支现金流", caption: "收入、消费与家庭现金流", icon: "cashflow" },
      { id: "fx", view: "fx", label: "换汇资金池", caption: "外币本金与汇兑收益", icon: "fx" },
    ],
  },
  {
    id: "research",
    label: "投资研究",
    caption: "基金、银行理财与中美宏观数据",
    icon: "research",
    children: [
      { id: "research-funds", view: "research", section: "funds", label: "基金研究", caption: "基金筛选、费率与风险", icon: "funds" },
      { id: "research-wealth", view: "research", section: "wealth", label: "银行理财", caption: "净值、开放规则与费用", icon: "wealth" },
      { id: "research-macro", view: "research", section: "macro", label: "美国宏观", caption: "美联储、通胀、就业与增长", icon: "macro" },
      { id: "research-macro-cn", view: "research", section: "macro_cn", label: "中国宏观", caption: "增长、流动性、外贸与人口", icon: "macro" },
    ],
  },
  {
    id: "data",
    label: "数据中心",
    caption: "连接、导入与安全维护",
    icon: "data",
    children: [
      { id: "data-connections", view: "data", section: "connections", label: "账户连接", caption: "邮箱、券商与数据连接", icon: "connections" },
      { id: "data-import", view: "data", section: "import", label: "账单与交易", caption: "文件、邮箱与受控机构交易接入", icon: "import" },
      { id: "data-records", view: "data", section: "records", label: "统一记录", caption: "交易与来源记录审计", icon: "records" },
      { id: "data-backup", view: "data", section: "backup", label: "数据导出", caption: "按需下载可携带的用户业务数据", icon: "backup" },
      { id: "data-security", view: "data", section: "security", label: "访问安全", caption: "会话、权限与安全事件", icon: "security" },
      { id: "data-settings", view: "data", section: "settings", label: "家庭设置", caption: "成员、账户与系统偏好", icon: "settings" },
    ],
  },
] as const;

export const workspaceViews = new Set<WorkspaceView>(workspaceNavigation.flatMap((group) => [
  ...(group.task ? [group.task.view] : []),
  ...(group.children?.map((task) => task.view) ?? []),
]));

const workspaceTasks = workspaceNavigation.flatMap((group) => [
  ...(group.task ? [group.task] : []),
  ...(group.children ?? []),
]);

export const workspaceRouteKeys = new Set<WorkspaceRouteKey>(workspaceTasks.map((task) => task.id));

export function isWorkspaceRouteKey(value: string | null | undefined): value is WorkspaceRouteKey {
  return Boolean(value && workspaceRouteKeys.has(value as WorkspaceRouteKey));
}

export function isWorkspaceRouteValid(view?: string | null, section?: string | null) {
  const normalizedView = view?.trim() ?? "";
  const normalizedSection = section?.trim() ?? "";
  if (!normalizedView) return !normalizedSection;
  if (normalizedView === "planning" && !normalizedSection) return true;
  if (normalizedView === "positions" && normalizedSection === "groups") return true;
  if (!workspaceViews.has(normalizedView as WorkspaceView)) return false;
  if (!normalizedSection) return true;
  return workspaceTasks.some((task) => task.view === normalizedView && task.section === normalizedSection);
}

export function isWorkspaceTaskActive(task: WorkspaceTask, view: WorkspaceView, section?: string | null) {
  if (task.view !== view) return false;
  if (task.section) return task.section === section;
  return !section || !workspaceNavigation.some((group) => group.children?.some((item) => item.view === view && item.section === section));
}

export function findWorkspaceTask(view: WorkspaceView, section?: string | null) {
  return workspaceTasks.find((task) => isWorkspaceTaskActive(task, view, section))
    ?? workspaceTasks.find((task) => task.view === view)
    ?? workspaceNavigation[0].task!;
}

export function workspaceRouteKey(view?: string | null, section?: string | null): WorkspaceRouteKey {
  const normalizedView = view?.trim() || "overview";
  const legacyPlanning = normalizedView === "planning";
  const resolvedView = legacyPlanning ? "positions" : normalizedView;
  const resolvedSection = legacyPlanning || section === "groups" ? "allocation" : section;
  if (!workspaceViews.has(resolvedView as WorkspaceView)) return "overview";
  return findWorkspaceTask(resolvedView as WorkspaceView, resolvedSection).id;
}

export function findWorkspaceGroup(view: WorkspaceView, section?: string | null) {
  const task = findWorkspaceTask(view, section);
  return workspaceNavigation.find((group) => group.task?.id === task.id || group.children?.some((item) => item.id === task.id))
    ?? workspaceNavigation[0];
}
